import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { ACCOUNTING, LoadingOverlay } from "./common";

const label = (code) => (code || "").replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const emptyForm = { booksBeginningDate: "", openingStockValue: "", defaultBankLedgerId: "", upiBankLedgerId: "", chequesThroughClearing: false };

/** Books-beginning date, opening stock, which bank ledgers receipts post to, and the posting sync. */
const AccountingSettings = () => {
  const [form, setForm] = useState(emptyForm);
  const [bankLedgers, setBankLedgers] = useState([]);
  const [lastSyncedAt, setLastSyncedAt] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState("");

  const apply = (settings) => {
    setForm({
      booksBeginningDate: settings.booksBeginningDate || "",
      openingStockValue: Number(settings.openingStockValue) ? String(settings.openingStockValue) : "",
      defaultBankLedgerId: settings.defaultBankLedgerId ? String(settings.defaultBankLedgerId) : "",
      upiBankLedgerId: settings.upiBankLedgerId ? String(settings.upiBankLedgerId) : "",
      chequesThroughClearing: Boolean(settings.chequesThroughClearing),
    });
    setLastSyncedAt(settings.lastSyncedAt || {});
  };

  const load = async () => {
    setLoading(true);
    try {
      const ledgers = await api.get(`${ACCOUNTING}/ledgers`);
      setBankLedgers((Array.isArray(ledgers) ? ledgers : []).filter((l) => l.group === "BANK_ACCOUNTS" || l.group === "BANK_OD"));
      apply(await api.get(`${ACCOUNTING}/settings`));
    } catch (error) {
      toast.error(error.message || "Failed to load accounting settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      apply(
        await api.put(`${ACCOUNTING}/settings`, {
          booksBeginningDate: form.booksBeginningDate || null,
          openingStockValue: form.openingStockValue === "" ? 0 : Number(form.openingStockValue),
          defaultBankLedgerId: form.defaultBankLedgerId ? Number(form.defaultBankLedgerId) : null,
          upiBankLedgerId: form.upiBankLedgerId ? Number(form.upiBankLedgerId) : null,
          chequesThroughClearing: form.chequesThroughClearing,
        })
      );
      toast.success("Accounting settings saved");
    } catch (error) {
      toast.error(error.message || "Failed to save accounting settings");
    } finally {
      setSaving(false);
    }
  };

  const runSync = async (rebuild) => {
    if (rebuild && !window.confirm("Rebuild re-posts every sales, purchase, collection and note voucher from scratch. Manual vouchers are not touched. Continue?")) return;
    setSyncing(rebuild ? "rebuild" : "sync");
    try {
      const result = await api.post(`${ACCOUNTING}/sync${rebuild ? "/rebuild" : ""}`);
      toast.success(`Books ${rebuild ? "rebuilt" : "synced"}: ${result.vouchersWritten} vouchers written, ${result.vouchersRemoved} removed`);
      apply(await api.get(`${ACCOUNTING}/settings`));
    } catch (error) {
      toast.error(error.message || "Sync failed");
    } finally {
      setSyncing("");
    }
  };

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));
  const sources = Object.entries(lastSyncedAt);

  return (
    <div className="p-4" style={{ position: "relative" }}>
      <LoadingOverlay show={loading} label="Loading settings..." />
      <form onSubmit={handleSubmit} className="content-card" style={{ maxWidth: "820px" }}>
        <h3 style={{ fontSize: "15px", fontWeight: 700 }}>Books</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Books Beginning Date</label>
            <input type="date" onClick={(e) => e.target.showPicker?.()} value={form.booksBeginningDate} onChange={set("booksBeginningDate")} />
            <span className="page-subtitle">Ledger opening balances and opening stock are as on this date.</span>
          </div>
          <div className="form-group">
            <label>Opening Stock Value (₹, at cost)</label>
            <input type="number" min="0" step="0.01" value={form.openingStockValue} onChange={set("openingStockValue")} />
            <span className="page-subtitle">Stock you already held on the books beginning date. Leave blank if you started with none.</span>
          </div>
        </div>

        <h3 style={{ fontSize: "15px", fontWeight: 700, marginTop: "24px" }}>Where collections are posted</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>Bank for cheque / e-fund receipts</label>
            <select value={form.defaultBankLedgerId} onChange={set("defaultBankLedgerId")}>
              <option value="">Default "Bank Account" ledger</option>
              {bankLedgers.map((ledger) => (
                <option key={ledger.id} value={ledger.id}>
                  {ledger.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Bank for UPI receipts</label>
            <select value={form.upiBankLedgerId} onChange={set("upiBankLedgerId")}>
              <option value="">Same as cheque / e-fund bank</option>
              {bankLedgers.map((ledger) => (
                <option key={ledger.id} value={ledger.id}>
                  {ledger.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ gridColumn: "1 / -1" }}>
            <label style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
              <input
                type="checkbox"
                checked={form.chequesThroughClearing}
                onChange={(e) => setForm((prev) => ({ ...prev, chequesThroughClearing: e.target.checked }))}
              />
              Hold cheques in "Cheques in Hand" until they are marked cleared
            </label>
            <span className="page-subtitle">Off (default): a cheque receipt is added to the bank balance as soon as it is collected.</span>
          </div>
        </div>
        <div className="page-subtitle" style={{ marginTop: "8px" }}>
          Add more bank accounts under Accounting → Ledgers (group "Bank Accounts"). Changing these re-posts existing collections on the next sync.
        </div>
        <div className="modal-actions">
          <button type="submit" className="primary" disabled={saving}>
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>

      <div className="content-card" style={{ maxWidth: "820px", marginTop: "20px" }}>
        <h3 style={{ fontSize: "15px", fontWeight: 700 }}>Posting sync</h3>
        <div className="page-subtitle" style={{ marginTop: "6px" }}>
          Invoices, collections and notes are posted to the books automatically whenever a report is opened. Use Sync to post now, or Rebuild after
          correcting old data.
        </div>
        <div className="filters-bar" style={{ marginTop: "12px" }}>
          <button type="button" className="primary" onClick={() => runSync(false)} disabled={Boolean(syncing)}>
            {syncing === "sync" ? "Syncing..." : "Sync Now"}
          </button>
          <button type="button" className="secondary" onClick={() => runSync(true)} disabled={Boolean(syncing)}>
            {syncing === "rebuild" ? "Rebuilding..." : "Rebuild All Postings"}
          </button>
        </div>
        {sources.length ? (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th style={{ textAlign: "left" }}>Document</th>
                  <th style={{ textAlign: "left" }}>Posted up to changes made at</th>
                </tr>
              </thead>
              <tbody>
                {sources.map(([source, at]) => (
                  <tr key={source}>
                    <td style={{ textAlign: "left" }}>{label(source)}</td>
                    <td style={{ textAlign: "left" }}>{at ? new Date(at).toLocaleString("en-IN") : "Nothing posted yet"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default AccountingSettings;
